import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";
import envConfig from "../../../config";
import ThemeToggle from "../../ThemeToggle/ThemeToggle";

const ChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDarkMode } = useTheme();

  const [open, setOpen] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [formData, setFormData] = useState({
    password: "",
    repeatPassword: "",
  });

  const [recoveryHash, setRecoveryHash] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const param = searchParams.get("recovery");
    if (param) {
      setRecoveryHash(param);
    } else {
      navigate("/");
    }
  }, [location.search, navigate]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (loading || !recoveryHash) return;

    const passwordRegex =
      /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.*\s).{10,}$/;
    if (!passwordRegex.test(formData.password)) {
      setAlertMessage(
        "At least 10 characters, including 1 uppercase, 1 lowercase, 1 digit and 1 special character."
      );
      setOpen(true);
      return;
    }

    if (formData.password !== formData.repeatPassword) {
      setAlertMessage("Passwords don't match");
      setOpen(true);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `https://${envConfig.apiDev1}/api/v3/en-au/users/update-login-password?BaseHostURL=${envConfig.loginUrl}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "olivs-api-real-ip": "115.70.37.103",
            "olivs-api-computer-name": "EC2AMAZ-QF1CQAD",
            "api-key": "OlivsAuth",
          },
          body: JSON.stringify({
            NewPasswordBase64: btoa(formData.password),
            ForgotPasswordHash: recoveryHash,
          }),
        }
      );
      const data = await response.json();

      if (data?.SuccessYN === "Y") {
        setSuccessMessage(
          "Password changed successfully. You will be redirected soon"
        );
        setOpenSuccess(true);
        setTimeout(() => {
          navigate("/");
        }, 5000);
      } else {
        setAlertMessage(data.ErrorMessage || "An error occurred");
        setOpen(true);
      }
    } catch (error) {
      setAlertMessage("An error occurred");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleCloseSuccess = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSuccess(false);
  };

  if (!recoveryHash) {
    return null;
  }

  return (
    <Box className="glass-card" sx={{ p: { xs: 3.5, md: 4 }, width: "100%" }}>
      <Snackbar
        sx={{ zIndex: 9999999 }}
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        data-testid="change-password--Snackbar-0"
      >
        <Alert
          onClose={handleClose}
          severity="error"
          data-testid="change-password--Alert-0"
        >
          {alertMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        sx={{ zIndex: 9999999 }}
        open={openSuccess}
        autoHideDuration={6000}
        onClose={handleCloseSuccess}
        data-testid="change-password--Snackbar-1"
      >
        <Alert
          onClose={handleCloseSuccess}
          severity="success"
          data-testid="change-password--Alert-1"
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Typography variant="h2" sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}>
        Change Password
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
        Enter your new password below.
      </Typography>

      <Box component="form" onSubmit={handleFormSubmit}>
        <TextField
          fullWidth
          label="New Password"
          type="password"
          required
          name="password"
          value={formData.password}
          onChange={handleChange}
          sx={{ mb: 1 }}
          data-testid="change-password--TextField-0"
        />
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5 }}>
          At least 10 characters, including 1 uppercase, 1 lowercase, 1 digit and 1 special character.
        </Typography>

        <TextField
          fullWidth
          label="Repeat New Password"
          type="password"
          required
          name="repeatPassword"
          value={formData.repeatPassword}
          onChange={handleChange}
          sx={{ mb: 3 }}
          data-testid="change-password--TextField-1"
        />

        <Button
          type="submit"
          variant="outlined"
          className="btn-glass"
          disabled={loading}
          fullWidth
          sx={{ mb: 3 }}
          data-testid="change-password--Button-0"
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
        </Button>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <ThemeToggle title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"} />
        </Box>
      </Box>
    </Box>
  );
};

export default ChangePassword;
